/**
 * トップ「わたしたち」エリアの `.keywordBox` 内テキストを、
 * ボックスの端から端までランダムな位置へ向かって動かし続ける処理です。
 *
 * 対象: `.homeMv-staff .keywordBox`（MV 内スライド）、`.homeStaff .keywordBox`（下層スタッフセクション）
 * - 位置は `transform: translate()` で更新（レイアウト再計算を避ける）
 * - `.homeStaff` は表示中（.is-active）の keywordBox のみループを走らせる
 * - `ResizeObserver` で `.keywordBox` のサイズ変化を監視し、移動可能範囲を再計算
 */

// --- 調整用：移動の速さ（値が大きいほど速い。単位は「px / ミリ秒」に近いスケール）---
const SPEED = {
  pc: {
    initialMin: 0.030,
    initialSpread: 0.030,
    targetMin: 0.030,
    targetSpread: 0.030,
  },
  sp: {
    initialMin: 0.024,
    initialSpread: 0.022,
    targetMin: 0.024,
    targetSpread: 0.022,
  },
};

const SP_MQ = window.matchMedia('(max-width: 768px)');

function getSpeedSettings() {
  return SP_MQ.matches ? SPEED.sp : SPEED.pc;
}

function randomSpeed(isInitial) {
  const s = getSpeedSettings();
  if (isInitial) {
    return s.initialMin + Math.random() * s.initialSpread;
  }
  return s.targetMin + Math.random() * s.targetSpread;
}

const POSITION_EDGE_ZONE_RATIO = 0.18;
const POSITION_EDGE_STRIP_MIN_RATIO = 0.015;
const FRAME_DELTA_MAX_MS = 32;

function applyParticlePosition(p) {
  // サブピクセルで更新（整数丸めは低速時にガタつく）。overflow:hidden で横ぶれは防ぐ
  p.el.style.transform = `translate3d(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px, 0)`;
}

function getMovableMaxForItem(box, el) {
  let innerW = box.clientWidth;
  let innerH = box.clientHeight;
  if (innerW <= 0 || innerH <= 0) {
    const rect = box.getBoundingClientRect();
    if (innerW <= 0) {
      innerW = rect.width;
    }
    if (innerH <= 0) {
      innerH = rect.height;
    }
  }

  const itemW = el.offsetWidth;
  const itemH = el.offsetHeight;

  return {
    maxX: Math.max(0, innerW - itemW - 1),
    maxY: Math.max(0, innerH - itemH - 1)
  };
}

function recalcParticleBounds(box, particles) {
  particles.forEach((p) => {
    const { maxX, maxY } = getMovableMaxForItem(box, p.el);

    p.maxX = maxX;
    p.maxY = maxY;
    p.x = Math.min(Math.max(0, p.x), maxX);
    p.y = Math.min(Math.max(0, p.y), maxY);
    p.tx = Math.min(Math.max(0, p.tx), maxX);
    p.ty = Math.min(Math.max(0, p.ty), maxY);
  });
}

function clamp01(v, max) {
  return Math.min(Math.max(0, v), max);
}

function edgeStripSize(axisMax) {
  if (axisMax <= 0) {
    return 0;
  }
  return Math.min(
    Math.max(axisMax * POSITION_EDGE_ZONE_RATIO, axisMax * POSITION_EDGE_STRIP_MIN_RATIO),
    axisMax * 0.495
  );
}

function samplePointOnRandomEdge(maxX, maxY) {
  if (maxX <= 0 && maxY <= 0) {
    return { x: 0, y: 0 };
  }
  if (maxX <= 0) {
    return { x: 0, y: Math.random() * maxY };
  }
  if (maxY <= 0) {
    return { x: Math.random() * maxX, y: 0 };
  }

  const sx = edgeStripSize(maxX);
  const sy = edgeStripSize(maxY);
  const side = Math.floor(Math.random() * 4);
  let x;
  let y;

  if (side === 0) {
    x = Math.random() * sx;
    y = Math.random() * maxY;
  } else if (side === 1) {
    x = maxX - Math.random() * sx;
    y = Math.random() * maxY;
  } else if (side === 2) {
    x = Math.random() * maxX;
    y = Math.random() * sy;
  } else {
    x = Math.random() * maxX;
    y = maxY - Math.random() * sy;
  }

  return { x: clamp01(x, maxX), y: clamp01(y, maxY) };
}

function sampleTargetOppositeFrom(maxX, maxY, cx, cy) {
  if (maxX <= 0 && maxY <= 0) {
    return { x: 0, y: 0 };
  }
  if (maxX <= 0) {
    const sy = edgeStripSize(maxY);
    const midY = maxY / 2;
    const ty = cy <= midY ? maxY - Math.random() * sy : Math.random() * sy;
    return { x: 0, y: clamp01(ty, maxY) };
  }
  if (maxY <= 0) {
    const sx = edgeStripSize(maxX);
    const midX = maxX / 2;
    const tx = cx <= midX ? maxX - Math.random() * sx : Math.random() * sx;
    return { x: clamp01(tx, maxX), y: 0 };
  }

  const sx = edgeStripSize(maxX);
  const sy = edgeStripSize(maxY);
  const horizontal = Math.random() < maxX / (maxX + maxY);

  if (horizontal) {
    const midX = maxX / 2;
    const tx = cx <= midX ? maxX - Math.random() * sx : Math.random() * sx;
    const ty = Math.random() * maxY;
    return { x: clamp01(tx, maxX), y: clamp01(ty, maxY) };
  }

  const midY = maxY / 2;
  const ty = cy <= midY ? maxY - Math.random() * sy : Math.random() * sy;
  const tx = Math.random() * maxX;
  return { x: clamp01(tx, maxX), y: clamp01(ty, maxY) };
}

function destroyKeywordBox(box) {
  if (!box) {
    return;
  }
  if (box.__floatRafId) {
    window.cancelAnimationFrame(box.__floatRafId);
    box.__floatRafId = 0;
  }
  if (box.__keywordBoxResizeObserver) {
    box.__keywordBoxResizeObserver.disconnect();
    box.__keywordBoxResizeObserver = null;
  }
  delete box.dataset.floatReady;
}

function initKeywordBox(box, attempt = 0) {
  if (!box || box.dataset.floatReady === '1') {
    return;
  }

  const items = box.querySelectorAll('span');
  if (!items.length) {
    return;
  }

  // 非表示→表示直後は寸法が 0 のことがあるため、数フレーム再試行
  const { maxX, maxY } = getMovableMaxForItem(box, items[0]);
  if (maxX <= 0 && maxY <= 0) {
    if (attempt < 240) {
      window.requestAnimationFrame(() => initKeywordBox(box, attempt + 1));
    }
    return;
  }

  box.dataset.floatReady = '1';

  const particles = [];

  items.forEach((item) => {
    const { maxX, maxY } = getMovableMaxForItem(box, item);

    const start = samplePointOnRandomEdge(maxX, maxY);
    const startX = start.x;
    const startY = start.y;

    item.style.left = '0';
    item.style.top = '0';
    item.style.right = 'auto';
    item.style.bottom = 'auto';
    item.style.willChange = 'transform';
    item.style.backfaceVisibility = 'hidden';

    const firstTarget = sampleTargetOppositeFrom(maxX, maxY, startX, startY);

    particles.push({
      el: item,
      x: startX,
      y: startY,
      tx: firstTarget.x,
      ty: firstTarget.y,
      speed: randomSpeed(true),
      maxX,
      maxY
    });

    applyParticlePosition(particles[particles.length - 1]);
  });

  let boundsRafId = 0;
  const scheduleBoundsRecalc = () => {
    if (boundsRafId) {
      return;
    }
    boundsRafId = window.requestAnimationFrame(() => {
      boundsRafId = 0;
      recalcParticleBounds(box, particles);
    });
  };

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(scheduleBoundsRecalc);
    resizeObserver.observe(box);
    box.__keywordBoxResizeObserver = resizeObserver;
  }

  let rafId = 0;
  let last = 0;

  const randomTarget = (p) => {
    const next = sampleTargetOppositeFrom(p.maxX, p.maxY, p.x, p.y);
    p.tx = next.x;
    p.ty = next.y;
    p.speed = randomSpeed(false);
  };

  const tick = (now) => {
    if (document.hidden) {
      last = 0;
      rafId = window.requestAnimationFrame(tick);
      box.__floatRafId = rafId;
      return;
    }

    if (!last) {
      last = now;
    }
    const dtMs = Math.min(FRAME_DELTA_MAX_MS, Math.max(0, now - last));
    last = now;

    particles.forEach((p) => {
      const dx = p.tx - p.x;
      const dy = p.ty - p.y;
      const distance = Math.hypot(dx, dy);
      const step = p.speed * dtMs;

      if (distance <= step || distance < 0.5) {
        p.x = p.tx;
        p.y = p.ty;
        randomTarget(p);
      } else {
        p.x += (dx / distance) * step;
        p.y += (dy / distance) * step;
      }

      applyParticlePosition(p);
    });

    rafId = window.requestAnimationFrame(tick);
    box.__floatRafId = rafId;
  };

  rafId = window.requestAnimationFrame(tick);
  box.__floatRafId = rafId;
}

/** MV 内 Splide も表示中スライドの keywordBox のみ初期化する */
function getHomeMvStaffSplideRoots(scope) {
  if (
    scope instanceof Element &&
    (scope.id === 'homeMvStaffSplide' || scope.classList.contains('homeMv-staff-splide'))
  ) {
    return [scope];
  }
  return (scope || document).querySelectorAll('#homeMvStaffSplide, .homeMv-staff-splide');
}

function findActiveMvStaffKeywordBox(splideRoot, slideIndex) {
  const slides = splideRoot.querySelectorAll('.homeMv-staff-list-item');

  if (typeof slideIndex === 'number' && slideIndex >= 0 && slides[slideIndex]) {
    return slides[slideIndex].querySelector('.keywordBox');
  }

  // fade 中は複数 .is-visible になり得るため、最後に一致したスライドを採用
  const visibleSlides = splideRoot.querySelectorAll('.homeMv-staff-list-item.is-active.is-visible');
  if (visibleSlides.length) {
    return visibleSlides[visibleSlides.length - 1].querySelector('.keywordBox');
  }

  const activeSlides = splideRoot.querySelectorAll('.homeMv-staff-list-item.is-active');
  if (activeSlides.length) {
    return activeSlides[activeSlides.length - 1].querySelector('.keywordBox');
  }

  return slides[0]?.querySelector('.keywordBox') ?? null;
}

function syncHomeMvStaffKeywords(scope, slideIndex) {
  getHomeMvStaffSplideRoots(scope).forEach((splideRoot) => {
    splideRoot.querySelectorAll('.homeMv-staff-list-item .keywordBox').forEach((box) => {
      destroyKeywordBox(box);
    });
    initKeywordBox(findActiveMvStaffKeywordBox(splideRoot, slideIndex));
  });
}

/** `.homeStaff` は表示中スライドの keywordBox だけを初期化する */
function getHomeStaffSections(scope) {
  if (scope instanceof Element && scope.classList.contains('homeStaff')) {
    return [scope];
  }
  return (scope || document).querySelectorAll('.homeStaff');
}

function syncHomeStaffKeywords(scope) {
  getHomeStaffSections(scope).forEach((section) => {
    section.querySelectorAll('.homeStaff-content-main-list-item .keywordBox').forEach((box) => {
      destroyKeywordBox(box);
    });
    const activeBox = section.querySelector('.homeStaff-content-main-list-item.is-active .keywordBox');
    initKeywordBox(activeBox);
  });
}

function initHomeKeywords() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  document.querySelectorAll('.staffArchive-list-item .keywordBox').forEach((box) => {
    destroyKeywordBox(box);
    initKeywordBox(box);
  });

  syncHomeMvStaffKeywords();
  syncHomeStaffKeywords();
}

initHomeKeywords();
document.addEventListener('swup:replaced', initHomeKeywords);
document.addEventListener('homeMvStaff:slide-changed', (event) => {
  const { root, index } = event.detail ?? {};
  syncHomeMvStaffKeywords(root || document, index);
});
document.addEventListener('homeStaff:active-changed', (event) => {
  syncHomeStaffKeywords(event.detail?.root || document);
});
document.addEventListener('home:gate-released', () => {
  syncHomeMvStaffKeywords();
  syncHomeStaffKeywords();
});
